import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CreateSubtenantPayload, SubTenant, UpdateSubtenantPayload } from '../models/subtenant.model';

const SUBTENANT_TOKEN_KEY = 'subtenant_token';
const SUBTENANTS_EMPTY_KEY = 'subtenants_empty';
const SELECTED_SUBTENANT_ID_KEY = 'selected_subtenant_id';
const SELECTED_SUBTENANT_NAME_KEY = 'selected_subtenant_name';

@Injectable({ providedIn: 'root' })
export class SubTenantService {
    private readonly endpoint: string = environment.apiUrl;

    readonly subtenantToken = signal<string | null>(sessionStorage.getItem(SUBTENANT_TOKEN_KEY));
    readonly selectedSubtenantId = signal<string | null>(sessionStorage.getItem(SELECTED_SUBTENANT_ID_KEY));
    readonly selectedSubtenantName = signal<string | null>(sessionStorage.getItem(SELECTED_SUBTENANT_NAME_KEY));
    readonly subtenantsChecked = signal<boolean>(
        !!sessionStorage.getItem(SUBTENANT_TOKEN_KEY) ||
        sessionStorage.getItem(SUBTENANTS_EMPTY_KEY) === 'true'
    );
    readonly hasSubtenants = signal<boolean>(!!sessionStorage.getItem(SUBTENANT_TOKEN_KEY));

    constructor(private http: HttpClient) {}

    getSubtenants(): Observable<SubTenant[]> {
        return this.http.get<SubTenant[]>(`${this.endpoint}/user/subtenants`);
    }

    createSubtenant(payload: CreateSubtenantPayload): Observable<SubTenant> {
        return this.http.post<SubTenant>(
            `${this.endpoint}/user/subtenants/create`,
            payload
        );
    }

    updateSubtenant(payload: UpdateSubtenantPayload): Observable<SubTenant> {
        return this.http.put<SubTenant>(
            `${this.endpoint}/user/subtenants/update`,
            payload
        );
    }

    deleteSubtenant(id: string): Observable<unknown> {
        return this.http.delete(
            `${this.endpoint}/user/subtenants/delete`,
            { body: { id: String(id) } }
        );
    }

    selectSubtenant(subtenantId: string): Observable<string> {
        return this.http.post(
            `${this.endpoint}/user/select-subtenant`,
            { subtenantId },
            { responseType: 'text' }
        ).pipe(
            map((response: string) => this.extractToken(response)),
            tap((token: string) => {
                this.subtenantToken.set(token);
                this.selectedSubtenantId.set(subtenantId);
                this.subtenantsChecked.set(true);
                this.hasSubtenants.set(true);
                sessionStorage.setItem(SUBTENANT_TOKEN_KEY, token);
                sessionStorage.setItem(SELECTED_SUBTENANT_ID_KEY, subtenantId);
                sessionStorage.removeItem(SUBTENANTS_EMPTY_KEY);
            })
        );
    }

    getSelectedSubtenantId(): string | null {
        return this.selectedSubtenantId() ?? sessionStorage.getItem(SELECTED_SUBTENANT_ID_KEY);
    }

    setSelectedSubtenantName(name: string): void {
        this.selectedSubtenantName.set(name);
        sessionStorage.setItem(SELECTED_SUBTENANT_NAME_KEY, name);
    }

    setSelectedSubtenantId(id: string): void {
        this.selectedSubtenantId.set(id);
        sessionStorage.setItem(SELECTED_SUBTENANT_ID_KEY, id);
    }

    private extractToken(response: string): string {
        try {
            const parsed = JSON.parse(response);
            if (parsed && typeof parsed.token === 'string') {
                return parsed.token;
            }
        } catch {}
        return response;
    }

    markNoSubtenants(): void {
        this.subtenantsChecked.set(true);
        this.hasSubtenants.set(false);
        sessionStorage.setItem(SUBTENANTS_EMPTY_KEY, 'true');
    }

    setAvailableSubtenants(subtenants: SubTenant[]): void {
        this._availableSubtenants = subtenants;
    }

    getAvailableSubtenants(): SubTenant[] {
        return this._availableSubtenants;
    }

    private _availableSubtenants: SubTenant[] = [];

    clearSubtenant(): void {
        this.subtenantToken.set(null);
        this.selectedSubtenantId.set(null);
        this.selectedSubtenantName.set(null);
        this.subtenantsChecked.set(false);
        this.hasSubtenants.set(false);
        this._availableSubtenants = [];
        sessionStorage.removeItem(SUBTENANT_TOKEN_KEY);
        sessionStorage.removeItem(SELECTED_SUBTENANT_ID_KEY);
        sessionStorage.removeItem(SELECTED_SUBTENANT_NAME_KEY);
        sessionStorage.removeItem(SUBTENANTS_EMPTY_KEY);
    }
}
